import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SMS_LIMIT_MAX, SmsUsageStatus } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatNumber } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Button, Card, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { overviewKey, useTenantOverview } from './TenantPartnerSections';

/** "Text messages" card on the platform customer page: the monthly limit (edit) and this month's use. Reads the overview. */
export function TenantSmsSection({ tenantId }: { tenantId: string }) {
  const canManage = useCan('platform.tenants_manage');
  const q = useTenantOverview(tenantId);
  const qc = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState('');
  const current = q.data?.sms;
  useEffect(() => {
    if (current) setValue(String(current.limit));
  }, [current?.limit]); // eslint-disable-line react-hooks/exhaustive-deps

  const n = Number(value);
  const valid = value.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= SMS_LIMIT_MAX;
  const save = useMutation({
    mutationFn: () => api.put(`/platform/tenants/${tenantId}/sms`, { body: { smsMonthlyLimit: n }, schema: SmsUsageStatus }),
    onSuccess: async () => {
      toast(t('sms.platform.card.saved'));
      await qc.invalidateQueries({ queryKey: overviewKey(tenantId) });
    },
  });
  const errs = fieldErrors(save.error);

  return (
    <Card
      title={t('sms.platform.card.title')}
      description={t('sms.platform.card.help')}
      footer={
        canManage ? (
          <Button variant="primary" disabled={!valid || !current || n === current.limit || save.isPending} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
      <ErrorLine error={save.error && Object.keys(errs).length === 0 ? save.error : null} />
      {current ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label={t('sms.platform.card.limit')}
            type="number"
            min={0}
            max={SMS_LIMIT_MAX}
            step={1}
            value={value}
            readOnly={!canManage}
            error={errs.smsMonthlyLimit}
            onChange={(e) => setValue(e.target.value)}
          />
          <div>
            <p className="mb-1 text-sm font-medium text-slate-800">{t('sms.platform.card.used')}</p>
            <p className="text-2xl font-semibold text-slate-900">{formatNumber(current.used)}</p>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
